/**
 * What the Pipeline page's one-click Print/PDF/Excel toolbar icons render —
 * configured from the Reporting page's "Pipeline reports" section.
 */
import api from './api'
import type { PdfColumnCaps } from '@/pages/pipeline-intelligence/types'
import type { PipelineReportLayout } from '@/pages/pipeline-intelligence/screenLayout'
import { DEFAULT_PIPELINE_EXPORT_SETTINGS } from './pipelineExportDefaults'

/**
 * 'backshop' (the default): Print, PDF and Excel always export the
 * `pipeline_backshop` template — Backshop's 26 columns, deals under their stage —
 * whatever columns the screen shows. 'legacy': the behaviour before that
 * template existed, governed by `template` and `useOnScreenColumns`.
 */
export type PipelineExportLayout = 'backshop' | 'legacy'

export type PipelineExportSettings = {
  layout: PipelineExportLayout
  template: 'pipeline_summary' | 'pipeline_daily'
  formats: Array<'pdf' | 'xlsx'>
  useOnScreenColumns: boolean
  /**
   * The Backshop report's definition — its columns, their headers and the stage order — served by
   * the server, which is the only place it is written. Absent until the server has answered; the
   * Pipeline page and the Command Center draw their grids from it and never from a copy.
   */
  backshopLayout?: PipelineReportLayout
  /** Server-resolved PDF column caps (includes `PDF_MAX_COLUMNS_*` overrides). */
  pdfColumnCaps?: PdfColumnCaps
}

export const pipelineExportSettingsService = {
  get: () =>
    api.get<PipelineExportSettings>('/reports/pipeline-export-settings').then((r) => ({
      ...DEFAULT_PIPELINE_EXPORT_SETTINGS,
      ...r.data,
    })),

  update: (body: PipelineExportSettings) =>
    api.put<PipelineExportSettings>('/reports/pipeline-export-settings', body).then((r) => ({
      ...DEFAULT_PIPELINE_EXPORT_SETTINGS,
      ...r.data,
    })),
}
